import { otherAPIHeaders } from "../shared-resources/dumpData";
import {
  sortVersions,
  sortIndirectVersions,
} from "../pages/powerbi/versionOrder";
import {
  toReportApiFilters,
  isAllFilterSelected,
  withPeriodFiltersForDownload,
} from "../pages/powerbi/reports/filterRelationshipUtils";

const BASE = process.env.FINANCIAL_CLARITY_REPORTS_URL;

function fetchDropdown(path) {
  return fetch(`${BASE}${path}`, { method: "GET", headers: otherAPIHeaders })
    .then((res) => res.json())
    .catch(() => ({ data: [] }));
}

export const getFilterYearsAPI = () => fetchDropdown("/reports/years");
export const getFilterMonthsAPI = () => fetchDropdown("/reports/months");
export const getFilterQuartersAPI = () => fetchDropdown("/reports/quarters");
export const getFilterContractIdsAPI = () =>
  fetchDropdown("/reports/contract_ids");
export const getFilterContractNamesAPI = () =>
  fetchDropdown("/reports/contract_names");
export const getFilterCustomerNamesAPI = () =>
  fetchDropdown("/reports/customer_names");
export const getFilterOrgIdsAPI = () => fetchDropdown("/reports/org_ids");
export const getFilterPopEndDatesAPI = () =>
  fetchDropdown("/reports/pop_end_dates");
export const getFilterBusinessUnitsAPI = () =>
  fetchDropdown("/reports/business_units");
export const getFilterProjIdsAPI = () => fetchDropdown("/reports/proj_ids");
export const getFilterContractTypesAPI = () =>
  fetchDropdown("/reports/contract_types");
export const getFilterSectorsAPI = () => fetchDropdown("/reports/sectors");
export const getFilterCompetitionTypesAPI = () =>
  fetchDropdown("/reports/competition_types");
export const getFilterAwardTypesAPI = () =>
  fetchDropdown("/reports/award_types");
export const getFilterBusinessTypesAPI = () =>
  fetchDropdown("/reports/business_types");
export const getFilterEmployeeIdsAPI = () =>
  fetchDropdown("/reports/employee_ids");

/** Flat contract/CLIN relationship feed for client-side filter cascading */
export const getFilterRelationshipsAPI = () =>
  fetchDropdown("/reports/filter_relationships");

export function getAdjustedBillRateAPI(filters = {}) {
  const clean = toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "bu",
    "sector",
    "employeeId",
    "pc",
  ]);
  return fetch(
    `${BASE}/reports/adjusted_bill_rate/employee_bill_rate_details`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters: clean, params: {} }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadAdjustedBillRateAPI = (filters = {}) =>
  downloadBlob(
    "/reports/adjusted_bill_rate/employee_bill_rate_details/download",
    toReportApiFilters(filters, [
      "year",
      "contractId",
      "projectId",
      "bu",
      "sector",
      "employeeId",
      "pc",
    ]),
    "adjusted-bill-rate.xlsx",
  );

export function getExceptionReportAPI(filters = {}) {
  const clean = toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "bu",
    "sector",
    "employeeId",
  ]);
  return fetch(
    `${BASE}/reports/exception_report/contract_project_employee_exceptions`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters: clean, params: {} }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadExceptionReportAPI = (filters = {}) =>
  downloadBlob(
    "/reports/exception_report/contract_project_employee_exceptions/download",
    toReportApiFilters(filters, [
      "year",
      "contractId",
      "projectId",
      "bu",
      "sector",
      "employeeId",
    ]),
    "exception-report.xlsx",
  );

export function getForecastVersionsComparisonAPI(
  filters = {},
  leftVersion = "liveEac",
  rightVersion = "q2",
  key = "contractRevenue",
) {
  return fetch(`${BASE}/reports/pl_by_program/forecast_versions_comparison`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters,
      params: { leftVersion, rightVersion, key },
    }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getVersionsAPI(year) {
  const query = year ? `?year=${year}` : "";
  return fetchDropdown(`/reports/versions${query}`).then((res) => {
    if (res?.data && Array.isArray(res.data)) {
      return { ...res, data: sortVersions(res.data) };
    }
    return res;
  });
}

/** Same endpoint as getVersionsAPI, but without the canonical-order resort —
 *  callers that need the backend's own ordering (e.g. picking a default
 *  selection from the first few entries) should use this instead. */
export function getVersionsRawAPI(year) {
  const query = year ? `?year=${year}` : "";
  return fetchDropdown(`/reports/versions${query}`);
}

export function getCmActualsVsForecastByProjAPI(
  filters = {},
  version = "",
  key = "ALL",
) {
  const params = {};
  if (version) params.version = version;
  if (key != null && key !== "") params.key = key;
  return fetch(`${BASE}/reports/pl_by_program/cm_actuals_vs_forecast_by_proj`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters,
      params,
    }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getCmActualsVsForecastAPI(filters = {}) {
  return fetch(`${BASE}/reports/pl_by_program/cm_actuals_vs_forecast`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getActualsVsForecastByDateRangeAPI(filters = {}, params = {}) {
  return fetch(
    `${BASE}/reports/pl_by_program/actuals_vs_forecast_by_date_range`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters, params }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export function getActualsVsForecastByProjDateRangeAPI(
  filters = {},
  range = {},
  version = "",
  key = "ALL",
) {
  const params = { ...range };
  if (version) params.version = version;
  if (key != null && key !== "") params.key = key;
  return fetch(
    `${BASE}/reports/pl_by_program/actuals_vs_forecast_by_proj_date_range`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters, params }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export function getPsrTrendAPI(filters = {}) {
  return fetch(`${BASE}/reports/pl_by_program/psr_trend`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getBudgetVsCurrentForecastAPI(filters = {}) {
  return fetch(
    `${BASE}/reports/scenario_comparison_mom/budget_vs_current_forecast`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters, params: {} }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export function getScenarioMomNextYearForecastAPI(filters = {}) {
  return fetch(`${BASE}/reports/scenario_comparison_mom/next_year_forecast`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getPriorVsCurrentForecastAPI(filters = {}) {
  return fetch(
    `${BASE}/reports/scenario_comparison_mom/prior_vs_current_forecast`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters, params: {} }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export function getCompareForecastVersionsAPI(
  filters = {},
  rightVersion = null,
) {
  const params = rightVersion ? { rightVersion } : {};
  return fetch(
    `${BASE}/reports/scenario_comparison_mom/compare_forecast_versions`,
    {
      method: "POST",
      headers: otherAPIHeaders,
      body: JSON.stringify({ filters, params }),
    },
  )
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadBudgetVsCurrentForecastAPI = (f) =>
  downloadBlob(
    "/reports/scenario_comparison_mom/budget_vs_current_forecast/download",
    f,
    "budget-vs-current-forecast.xlsx",
  );
export const downloadPriorVsCurrentForecastAPI = (f) =>
  downloadBlob(
    "/reports/scenario_comparison_mom/prior_vs_current_forecast/download",
    f,
    "prior-vs-current-forecast.xlsx",
  );
export const downloadScenarioMomNextYearForecastAPI = (f) =>
  downloadBlob(
    "/reports/scenario_comparison_mom/next_year_forecast/download",
    f,
    "next-year-forecast.xlsx",
  );
export const downloadCompareForecastVersionsAPI = (
  filters = {},
  rightVersion = null,
) =>
  downloadBlob(
    "/reports/scenario_comparison_mom/compare_forecast_versions/download",
    filters,
    "compare-forecast-versions.xlsx",
    rightVersion ? { rightVersion } : {},
  );

export function getNextYearForecastAPI(filters = {}) {
  return fetch(`${BASE}/reports/pl_by_program/next_year_forecast`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

/* ── Scenario Comparison YoY (Yearly Report by Scenario) ── */
export function getScenarioYoyTwoYearComparisonAPI(filters = {}) {
  return fetch(`${BASE}/reports/scenario/two_year_comparison`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getScenarioYoyFullPlanOverYearAPI(filters = {}) {
  return fetch(`${BASE}/reports/scenario/full_plan_over_year`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadScenarioYoyTwoYearComparisonAPI = (f) =>
  downloadBlob(
    "/reports/scenario/two_year_comparison/download",
    f,
    "two-year-comparison.xlsx",
  );
export const downloadScenarioYoyFullPlanOverYearAPI = (f) =>
  downloadBlob(
    "/reports/scenario/full_plan_over_year/download",
    f,
    "full-plan-over-year.xlsx",
  );

/* ── Forecast Monthly Summary ── */
export function getForecastSummaryMonthlyAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/forecast_summary/monthly`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: version ? { version } : {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadForecastSummaryMonthlyAPI = (filters = {}, version = "") =>
  downloadBlob(
    "/reports/forecast_summary/monthly/download",
    filters,
    "forecast-monthly-summary.xlsx",
    version ? { version } : {},
  );

export function getRevGMBridgeAPI(
  filters = {},
  version1 = "budget",
  version2 = "q2",
) {
  return fetch(`${BASE}/reports/pl_by_program/rev_gm_bridge`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: { version1, version2 } }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getGMRevBridgeAPI(
  filters = {},
  version1 = "budget",
  version2 = "q2",
) {
  return fetch(`${BASE}/reports/pl_by_program/gm_rev_bridge`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: { version1, version2 } }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getPLMonthlyTrendAPI(filters = {}) {
  return fetch(`${BASE}/reports/pl_by_program/pl_monthly_trend`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getCmActualsVsBudgetAPI(filters = {}) {
  return fetch(`${BASE}/reports/pl_by_program/cm_actuals_vs_budget`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export async function downloadPLByProgramAPI(
  tabKey,
  filters = {},
  params = {},
) {
  const filterKeys = [
    "year",
    "monthName",
    "quarter",
    "orgId",
    "contractType",
    "clinLvl2",
    "businessType",
    "awardType",
    "competitionType",
    "bu",
    "popEndDate",
    "sector",
    "contractId",
    "projectId",
    "contractName",
    "customerName",
  ];
  const f = toReportApiFilters(filters, filterKeys);
  /* year is ignored server-side for actuals_vs_forecast_by_date_range (the period
     comes from params.start/endMonth/Year instead) — omit it so the payload
     doesn't imply a fixed fiscal year this tab doesn't use. */
  if (tabKey === "actuals_vs_forecast_by_date_range" || tabKey === "actuals_vs_forecast_by_proj_date_range") delete f.year;
  else if (f.year != null) f.year = String(f.year);

  const res = await fetch(`${BASE}/reports/pl_by_program/${tabKey}/download`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: f, params }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Download failed (${res.status})`);
  }
  return res.blob();
}

function postMonthlyTM(path, filters = {}) {
  // monthName/quarter deliberately excluded — the full fiscal year is always fetched once and
  // narrowed to the selected month/quarter client-side (see monthly-tm-hours/*.js), so changing
  // just the month or quarter filter never re-triggers this request.
  const clean = toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "employeeId",
    "bu",
  ]);
  return fetch(`${BASE}/reports/monthly_tm_hours${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const getMonthlyTMByPersonAPI = (filters) =>
  postMonthlyTM("/by_person", filters);
export const getMonthlyTMByLcatAPI = (filters) =>
  postMonthlyTM("/by_lcat", filters);
export const getMonthlyTMByPOPAPI = (filters) =>
  postMonthlyTM("/by_period_of_performance", filters);

function buildMonthlyTMFilters(filters = {}) {
  return toReportApiFilters(filters, [
    "year",
    "monthName",
    "quarter",
    "contractId",
    "projectId",
    "employeeId",
    "bu",
    "sector",
  ]);
}

export const downloadMonthlyTMByPersonAPI = (f) =>
  downloadBlob(
    "/reports/monthly_tm_hours/by_person/download",
    buildMonthlyTMFilters(f),
    "monthly-tm-hours-by-person.xlsx",
  );
export const downloadMonthlyTMByLcatAPI = (f) =>
  downloadBlob(
    "/reports/monthly_tm_hours/by_lcat/download",
    buildMonthlyTMFilters(f),
    "monthly-tm-hours-by-lcat.xlsx",
  );
export const downloadMonthlyTMByPOPAPI = (f) =>
  downloadBlob(
    "/reports/monthly_tm_hours/by_period_of_performance/download",
    buildMonthlyTMFilters(f),
    "monthly-tm-hours-by-pop.xlsx",
  );

function postTMProfitability(path, filters = {}) {
  // monthName/quarter deliberately excluded — the full fiscal year is always fetched once and
  // narrowed to the selected month/quarter client-side (see tm-profitability/Details.js), so
  // changing just the month or quarter filter never re-triggers this request.
  const clean = toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "employeeId",
    "bu",
    "sector",
  ]);
  return fetch(`${BASE}/reports/tm_profitability${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const getTMProfitabilityDetailsAPI = (filters) =>
  postTMProfitability("/details", filters);

function buildTMProfitabilityFilters(filters = {}) {
  return toReportApiFilters(filters, [
    "year",
    "monthName",
    "quarter",
    "contractId",
    "projectId",
    "employeeId",
    "bu",
    "sector",
  ]);
}

export const downloadTMProfitabilityDetailsAPI = (f) =>
  downloadBlob(
    "/reports/tm_profitability/details/download",
    buildTMProfitabilityFilters(f),
    "tm-profitability-details.xlsx",
  );

function resolveLaborUtilPopEndDate(popEndDate) {
  if (!popEndDate || isAllFilterSelected(popEndDate)) return null;
  if (!Array.isArray(popEndDate) && typeof popEndDate === "object") {
    if (popEndDate.start || popEndDate.end) return popEndDate;
    return null;
  }
  if (Array.isArray(popEndDate) && popEndDate.length > 0) {
    const sorted = [...popEndDate].sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }
  return null;
}

function buildLaborUtilizationFilters(filters = {}) {
  const clean = toReportApiFilters(filters, [
    "year",
    "contractId",
    "projectId",
    "orgId",
    "bu",
    "contractType",
    "clinLvl2",
    "competitionType",
    "awardType",
    "sector",
    "employeeId",
    "employeeType",
  ]);
  const pop = resolveLaborUtilPopEndDate(filters.popEndDate);
  if (pop) clean.popEndDate = pop;
  return clean;
}

function postLaborUtilization(path, filters = {}, signal) {
  return fetch(`${BASE}/reports/labor_utilization${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters: buildLaborUtilizationFilters(filters),
      params: {},
    }),
    signal,
  }).then((res) => res.json());
}

export const getLaborUtilizationSummaryAPI = (filters, signal) =>
  postLaborUtilization("/summary", filters, signal);
export const getLaborUtilizationDetailsAPI = (filters, signal) =>
  postLaborUtilization("/details", filters, signal);
export const getLaborUtilizationFutureEmplNotesAPI = (filters, signal) =>
  postLaborUtilization("/future_empl_notes", filters, signal);
export const getLaborUtilizationBuUtilizationAPI = (filters, signal) =>
  postLaborUtilization("/bu_utilization", filters, signal);

export const downloadLaborUtilizationSummaryAPI = (f) =>
  downloadBlob(
    "/reports/labor_utilization/summary/download",
    withPeriodFiltersForDownload(f, buildLaborUtilizationFilters(f)),
    "labor-utilization-summary.xlsx",
  );
export const downloadLaborUtilizationDetailsAPI = (f) =>
  downloadBlob(
    "/reports/labor_utilization/details/download",
    withPeriodFiltersForDownload(f, buildLaborUtilizationFilters(f)),
    "labor-utilization-details.xlsx",
  );
export const downloadLaborUtilizationFutureEmplNotesAPI = (f) =>
  downloadBlob(
    "/reports/labor_utilization/future_empl_notes/download",
    withPeriodFiltersForDownload(f, buildLaborUtilizationFilters(f)),
    "labor-utilization-future-empl-notes.xlsx",
  );
export const downloadLaborUtilizationBuUtilizationAPI = (f) =>
  downloadBlob(
    "/reports/labor_utilization/bu_utilization/download",
    withPeriodFiltersForDownload(f, buildLaborUtilizationFilters(f)),
    "labor-utilization-bu-utilization.xlsx",
  );

function buildFundingExpenditureFilters(filters = {}) {
  return toReportApiFilters(filters, [
    "year",
    "monthName",
    "quarter",
    "contractId",
    "projectId",
    "bu",
    "orgId",
    "contractName",
    "customerName",
    "altProjId",
    "sector",
    "competitionType",
    "awardType",
    "popEndDate",
  ]);
}

function postFundingExpenditure(path, filters = {}, signal) {
  return fetch(`${BASE}/reports/funding_expenditure${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters: buildFundingExpenditureFilters(filters),
      params: {},
    }),
    signal,
  }).then((res) => res.json());
}

export const getFundingExpenditureDetailsAPI = (filters, signal) =>
  postFundingExpenditure("/details", filters, signal);
export const getFundingExpenditureSummaryAPI = (filters, signal) =>
  postFundingExpenditure("/summary", filters, signal);

export const downloadFundingExpenditureDetailsAPI = (f) =>
  downloadBlob(
    "/reports/funding_expenditure/details/download",
    buildFundingExpenditureFilters(f),
    "funding-expenditure-details.xlsx",
  );

export function getWaterfallViewAPI(filters = {}, version = "") {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/waterfall_eac/waterfall_view`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters: cleanFilters,
      params: version ? { version } : {},
    }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

/* ── Contract Notes APIs ────────────────────────────────────────── */
function buildContractNotesBody(filters = {}, version = "") {
  const f = {};
  if (filters.year && String(filters.year) !== "All")
    f.year = String(filters.year);
  return { filters: f, params: version ? { version } : {} };
}

export function getContractNotesAllProjectsAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/contract_notes/all_projects`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildContractNotesBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getContractNotes22402API(filters = {}, version = "") {
  return fetch(`${BASE}/reports/contract_notes/contract_22402`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildContractNotesBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadContractNotesAllProjectsAPI = (
  filters = {},
  version = "",
) => {
  const body = buildContractNotesBody(filters, version);
  return downloadBlob(
    "/reports/contract_notes/all_projects/download",
    body.filters,
    "contract-notes-all-projects.xlsx",
    body.params,
  );
};

export const downloadContractNotes22402API = (filters = {}, version = "") => {
  const body = buildContractNotesBody(filters, version);
  return downloadBlob(
    "/reports/contract_notes/contract_22402/download",
    body.filters,
    "contract-notes-22402.xlsx",
    body.params,
  );
};

/* ── Variances by Contract ─────────────────────────────────────── */
const VARIANCES_BY_CONTRACT_ARRAY_KEYS = [
  "monthName",
  "contractId",
  "projectId",
  "bu",
  "orgId",
  "contractName",
  "customerName",
  "sector",
  "competitionType",
  "awardType",
  "businessType",
];

function resolveVariancesPopEndDate(popEndDate) {
  if (!popEndDate || isAllFilterSelected(popEndDate)) return null;
  if (!Array.isArray(popEndDate) && typeof popEndDate === "object") {
    if (popEndDate.start || popEndDate.end) return popEndDate;
    return null;
  }
  if (Array.isArray(popEndDate) && popEndDate.length > 0) {
    const sorted = [...popEndDate].sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }
  return null;
}

export function buildVariancesByContractBody(filters = {}, params = {}) {
  const f = toReportApiFilters(filters, [
    "year",
    ...VARIANCES_BY_CONTRACT_ARRAY_KEYS,
  ]);
  const popRange = resolveVariancesPopEndDate(filters.popEndDate);
  if (popRange) f.popEndDate = popRange;
  const p = {};
  if (params.version) p.version = params.version;
  if (params.toVersion) p.toVersion = params.toVersion;
  if (params.value) p.value = params.value;
  return { filters: f, params: p };
}

export function getVariancesByContractAPI(filters = {}, params = {}) {
  return fetch(`${BASE}/reports/variances_by_contract/summary`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildVariancesByContractBody(filters, params)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

/** Same request body / response shape as getVariancesByContractAPI — only the
    endpoint differs, backing the "Variances by Contract L2" tab. */
export function getVariancesByContractL2API(filters = {}, params = {}) {
  return fetch(`${BASE}/reports/variances_by_contract/summarybyL2`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildVariancesByContractBody(filters, params)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

/** @deprecated Use getVariancesByContractAPI */
export const getSummaryVsBudgetAPI = getVariancesByContractAPI;

/** Download endpoints keep `version`/`toVersion` (internal keys, same as the
    live fetch) and additionally want `from`/`to` — the version dropdowns'
    displayed labels — passed through as-is via `params.from`/`params.to`. */
function withDownloadFromTo(bodyParams, { from, to } = {}) {
  const out = { ...bodyParams };
  if (from) out.from = from;
  if (to) out.to = to;
  return out;
}

export const downloadVariancesByContractAPI = (filters = {}, params = {}) => {
  const body = buildVariancesByContractBody(filters, params);
  return downloadBlob(
    "/reports/variances_by_contract/summary/download",
    body.filters,
    "variances-by-contract.xlsx",
    withDownloadFromTo(body.params, params),
  );
};

/** Same request body / response shape as downloadVariancesByContractAPI — only the
    endpoint differs, backing the "Variances by Contract L2" tab. */
export const downloadVariancesByContractL2API = (filters = {}, params = {}) => {
  const body = buildVariancesByContractBody(filters, params);
  return downloadBlob(
    "/reports/variances_by_contract/summarybyL2/download",
    body.filters,
    "variances-by-contract-l2.xlsx",
    withDownloadFromTo(body.params, params),
  );
};

/* ── Indirect dropdown APIs ─────────────────────────────────────── */
export const getIndirectVersionsAPI = () =>
  fetch(`${BASE}/reports/indirect/versions`, {
    method: "GET",
    headers: otherAPIHeaders,
  })
    .then((res) => res.json())
    .then((res) => {
      if (Array.isArray(res)) return sortIndirectVersions(res);
      if (Array.isArray(res?.data)) {
        return { ...res, data: sortIndirectVersions(res.data) };
      }
      return res;
    })
    .catch(() => []);

export const getIndirectFilterRelationshipsAPI = () =>
  fetch(`${BASE}/reports/indirect/filter_relationships`, {
    method: "GET",
    headers: otherAPIHeaders,
  })
    .then((res) => res.json())
    .catch(() => ({ data: [] }));

export const getIndirectFringeFilterRelationshipsAPI = () =>
  fetch(`${BASE}/reports/indirect/fringe/filter_relationships`, {
    method: "GET",
    headers: otherAPIHeaders,
  })
    .then((res) => res.json())
    .catch(() => ({ data: [] }));

export const getIndirectDepartmentsAPI = () =>
  fetchDropdown("/reports/indirect/departments");
export const getIndirectSubDepartmentsAPI = () =>
  fetchDropdown("/reports/indirect/sub-departments");
export const getIndirectEmployeeNamesAPI = () =>
  fetchDropdown("/reports/indirect/employee-names");
export const getIndirectCostTypeIndirectAPI = () =>
  fetchDropdown("/reports/indirect/cost_type_indirect");
export const getIndirectCostTypeFringeAPI = () =>
  fetchDropdown("/reports/indirect/cost-type-fringe");
export const getIndirectLaborComparisonAPI = (f) =>
  postIndirect("/labor/comparison", f);
export const getIndirectLaborTableAPI = (f) => postIndirect("/labor/table", f);
export const getIndirectLaborBudgetTableAPI = (f) =>
  postIndirect("/labor/budget/table", f);
export const getIndirectSummaryVsBudgetFringeAPI = (f) =>
  postIndirect("/summary-vs-budget-fringe", f);
export const getIndirectSummaryVsPriorFringeAPI = (f) =>
  postIndirect("/summary-vs-prior-fringe", f);
export const getIndirectSummaryVsBudgetDeptAPI = (f) =>
  postIndirect("/summary-vs-budget", f);
export const getIndirectSummaryVersionComparisonAPI = (f) =>
  postIndirect("/summary-version-comparison", f);
export const getIndirectSummaryVersionComparisonFringeAPI = (f) =>
  postIndirect("/summary-version-comparison-fringe", f);

/* ── Indirect data APIs ─────────────────────────────────────────── */
function postIndirect(path, filters = {}) {
  return fetch(`${BASE}/reports/indirect${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const getIndirectLaborMonthsAPI = (f) => postIndirect("/labor", f);
export const getIndirectLaborBudgetMonthsAPI = (f) =>
  postIndirect("/labor/budget", f);
export const getIndirectNonLaborMonthsAPI = (f) =>
  postIndirect("/non-labor", f);
export const getIndirectNonLaborBudgetMonthsAPI = (f) =>
  postIndirect("/non-labor/budget", f);
export const getIndirectFringeMonthsAPI = (f) => postIndirect("/fringe", f);
export const getIndirectFringeBudgetMonthsAPI = (f) =>
  postIndirect("/fringe/budget", f);
export const getFringeForecastVersionComparisonAPI = (f) =>
  postIndirect("/fringe-forecast-version-comparison", f);
export const getIndirectFringeAccountsAPI = () =>
  fetchDropdown("/reports/indirect/fringe/accounts");

export function getEACVarianceViewAPI(filters = {}, version = "") {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/waterfall_eac/eac_variance_view`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters: cleanFilters,
      params: { version: version || "budget" },
    }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getEACVarianceViewLevel2API(filters = {}, version = "") {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/waterfall_eac/eac_variance_view_level_2`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: cleanFilters, params: { version } }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getThreeYearViewAPI(filters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/waterfall_eac/three_year_view`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: cleanFilters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

// { filters, params: { version } }, and eac_variance_view(_level_2) 400s without a real
// params.version (it's required there, unlike the optional version on waterfall_view(_level_2)).
export const downloadWaterfallViewAPI = (filters = {}, version = "") =>
  downloadBlob(
    "/reports/waterfall_eac/waterfall_view/download",
    filters,
    "waterfall-view.xlsx",
    version ? { version } : {},
  );
export const downloadWaterfallViewLevel2API = (filters = {}, version = "") =>
  downloadBlob(
    "/reports/waterfall_eac/waterfall_view_level_2/download",
    filters,
    "waterfall-view-level-2.xlsx",
    version ? { version } : {},
  );
export const downloadEACVarianceViewAPI = (filters = {}, version = "") =>
  downloadBlob(
    "/reports/waterfall_eac/eac_variance_view/download",
    filters,
    "eac-variance-view.xlsx",
    { version },
  );
export const downloadEACVarianceViewLevel2API = (filters = {}, version = "") =>
  downloadBlob(
    "/reports/waterfall_eac/eac_variance_view_level_2/download",
    filters,
    "eac-variance-view-level-2.xlsx",
    { version },
  );
export const downloadThreeYearViewAPI = (filters = {}) =>
  downloadBlob(
    "/reports/waterfall_eac/three_year_view/download",
    filters,
    "three-year-view.xlsx",
  );

export function getExecutiveSummaryAPI(filters = {}, params = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/pl_by_program/executive_summary`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: cleanFilters, params }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getLtmKpiAPI(filters = {}, params = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, v]) => v !== "" && v != null),
  );
  return fetch(`${BASE}/reports/pl_by_program/ltm_kpi`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: cleanFilters, params: cleanParams }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getOverviewSectorAPI(filters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/pl_by_program/overview_sector`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: cleanFilters, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getProgramManagersAPI() {
  return fetchDropdown("/reports/program_managers");
}

export function getL2HeatmapCurrentYearAPI(filters = {}) {
  const clean = toReportApiFilters(filters, ["year", "programManager"]);
  return fetch(`${BASE}/reports/l2_tech_heatmap/current_year_heatmap`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getL2HeatmapYTDAPI(filters = {}) {
  const clean = toReportApiFilters(filters, ["year", "programManager"]);
  return fetch(`${BASE}/reports/l2_tech_heatmap/ytd_l2_report`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getL2HeatmapCurrentMonthAPI(filters = {}) {
  const clean = toReportApiFilters(filters, ["year", "programManager"]);
  return fetch(`${BASE}/reports/l2_tech_heatmap/current_month_l2_report`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

function buildL2HeatmapFilters(filters = {}) {
  return toReportApiFilters(filters, ["year", "programManager"]);
}

export const downloadL2HeatmapCurrentYearAPI = (f) =>
  downloadBlob(
    "/reports/l2_tech_heatmap/current_year_heatmap/download",
    buildL2HeatmapFilters(f),
    "l2-current-year-heatmap.xlsx",
  );
export const downloadL2HeatmapYTDAPI = (f) =>
  downloadBlob(
    "/reports/l2_tech_heatmap/ytd_l2_report/download",
    buildL2HeatmapFilters(f),
    "l2-ytd-report.xlsx",
  );
export const downloadL2HeatmapCurrentMonthAPI = (f, monthName) => {
  const filters = buildL2HeatmapFilters(f);
  /* This tab has no user-facing month filter — it always reports on the
     report's own already-derived "current" month, so the caller passes
     that computed month name through explicitly. */
  if (monthName) filters.monthName = [monthName];
  return downloadBlob(
    "/reports/l2_tech_heatmap/current_month_l2_report/download",
    filters,
    "l2-current-month-report.xlsx",
  );
};

function buildHeatmapBody(filters = {}) {
  const f = toReportApiFilters(filters, [
    "year",
    "contractType",
    "clinLvl2",
    "contractId",
    "sector",
    "bu",
    "programManager",
    "businessType",
    "awardType",
    "competitionType",
  ]);
  if (
    filters.popEndDate &&
    !isAllFilterSelected(filters.popEndDate) &&
    !Array.isArray(filters.popEndDate) &&
    (filters.popEndDate.start || filters.popEndDate.end)
  ) {
    f.popEndDate = filters.popEndDate;
  } else if (
    Array.isArray(filters.popEndDate) &&
    !isAllFilterSelected(filters.popEndDate) &&
    filters.popEndDate.length
  ) {
    const sorted = [...filters.popEndDate].sort();
    f.popEndDate = { start: sorted[0], end: sorted[sorted.length - 1] };
  }
  return { filters: f, params: {} };
}

export function getHeatmapCurrentYearAPI(filters = {}) {
  return fetch(`${BASE}/reports/heatmap/current_year_heatmap`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildHeatmapBody(filters)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getHeatmapYTDAPI(filters = {}) {
  return fetch(`${BASE}/reports/heatmap/ytd_heatmap`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildHeatmapBody(filters)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getHeatmapCurrentMonthAPI(filters = {}) {
  return fetch(`${BASE}/reports/heatmap/current_month_heatmap`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildHeatmapBody(filters)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadHeatmapCurrentMonthAPI = (f) =>
  downloadBlob(
    "/reports/heatmap/current_month_heatmap/download",
    withPeriodFiltersForDownload(f, buildHeatmapBody(f).filters),
    "current-month-heatmap.xlsx",
  );
export const downloadHeatmapCurrentYearAPI = (f) =>
  downloadBlob(
    "/reports/heatmap/current_year_heatmap/download",
    withPeriodFiltersForDownload(f, buildHeatmapBody(f).filters),
    "current-year-heatmap.xlsx",
  );
export const downloadHeatmapYTDAPI = (f) =>
  downloadBlob(
    "/reports/heatmap/ytd_heatmap/download",
    withPeriodFiltersForDownload(f, buildHeatmapBody(f).filters),
    "ytd-heatmap.xlsx",
  );

function buildDFRBody(filters = {}, version = "") {
  const f = {};
  if (filters.year && String(filters.year) !== "All")
    f.year = String(filters.year);
  /* monthName / quarter are applied client-side — do not send to API */
  [
    "contractId",
    "projectId",
    "bu",
    "orgId",
    "contractType",
    "businessType",
    "awardType",
    "competitionType",
    "popEndDate",
    "sector",
    "contractName",
  ].forEach((k) => {
    if (Array.isArray(filters[k]) && filters[k].length > 0) f[k] = filters[k];
  });
  return { filters: f, params: version ? { version } : {} };
}

export function getDFRSummaryAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/monthly_report_by_scenario/summary`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildDFRBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getDFRDetailsAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/monthly_report_by_scenario/details`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildDFRBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

/* `versionName` is the exact dropdown selection (e.g. "Budget 2027 v1"), sent alongside the
   `version` key for the Excel's title/header. */
const withVersionName = (params, versionName) =>
  versionName ? { ...params, versionName } : params;

export const downloadDFRSummaryAPI = (filters = {}, version = "", versionName = "") => {
  const body = buildDFRBody(filters, version);
  return downloadBlob(
    "/reports/monthly_report_by_scenario/summary/download",
    withPeriodFiltersForDownload(filters, body.filters),
    "monthly-report-by-scenario-summary.xlsx",
    withVersionName(body.params, versionName),
  );
};

export const downloadDFRDetailsAPI = (filters = {}, version = "", versionName = "") => {
  const body = buildDFRBody(filters, version);
  return downloadBlob(
    "/reports/monthly_report_by_scenario/details/download",
    withPeriodFiltersForDownload(filters, body.filters),
    "monthly-report-by-scenario-details.xlsx",
    withVersionName(body.params, versionName),
  );
};

/* Monthly Report by Scenario — Summary by Date Range. See
   APIIntegrationFiles/MONTHLY_REPORT_BY_SCENARIO_SUMMARY_BY_DATE_RANGE_API.md. Same contract-level
   filters as /summary; year / quarter / monthName are ignored by the backend, so they are not
   sent — the period comes solely from params. */
function buildDFRDateRangeBody(filters = {}, dateRange = {}, version = "") {
  const { startMonth, startYear, endMonth, endYear } = dateRange;
  const { year, ...rest } = buildDFRBody(filters, version).filters;
  return {
    filters: rest,
    params: { startMonth, startYear, endMonth, endYear, ...(version ? { version } : {}) },
  };
}

export function getDFRSummaryByDateRangeAPI(filters = {}, dateRange = {}, version = "") {
  return fetch(`${BASE}/reports/monthly_report_by_scenario/summary_by_date_range`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildDFRDateRangeBody(filters, dateRange, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadDFRSummaryByDateRangeAPI = (filters = {}, dateRange = {}, version = "", versionName = "") => {
  const body = buildDFRDateRangeBody(filters, dateRange, version);
  return downloadBlob(
    "/reports/monthly_report_by_scenario/summary_by_date_range/download",
    body.filters,
    "MonthlyReportByScenario_SummaryByDateRange.xlsx",
    withVersionName(body.params, versionName),
  );
};

/* ── AOP Budget (AD-793) ── See APIIntegrationFiles/AOP_BUDGET_API.md.
   `params.version` selects Table 1 (Summary)/Details' version for NEXT
   fiscal year (year filter + 1) — "budget" resolves that year's own Budget
   version specifically, not the generic "budget of the open version's
   year" `version` means elsewhere in this API. Table 2 (Summary) always
   uses Live EAC regardless of this param. Same curated filter set as
   Monthly DFR's own DFR_FILTER_KEYS — kept in sync with AOPBudget.js's
   AOP_FILTER_KEYS (the filter bar can never populate a key not in this
   list, so forwarding more here would be dead weight). */
const AOP_BUDGET_FILTER_KEYS = [
  "year", "contractType", "contractId", "clinLvl2",
  "orgId", "businessType", "awardType", "competitionType", "bu",
  "popEndDate", "projectId",
];

function buildAOPBudgetBody(filters = {}, version = "") {
  return {
    filters: toReportApiFilters(filters, AOP_BUDGET_FILTER_KEYS),
    params: version ? { version } : {},
  };
}

export function getAOPBudgetSummaryAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/aop_budget/summary`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildAOPBudgetBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export function getAOPBudgetDetailsAPI(filters = {}, version = "") {
  return fetch(`${BASE}/reports/aop_budget/details`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildAOPBudgetBody(filters, version)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const downloadAOPBudgetSummaryAPI = (filters = {}, version = "") => {
  const body = buildAOPBudgetBody(filters, version);
  return downloadBlob("/reports/aop_budget/summary/download", body.filters, "aop-budget-summary.xlsx", body.params);
};

export const downloadAOPBudgetDetailsAPI = (filters = {}, version = "") => {
  const body = buildAOPBudgetBody(filters, version);
  return downloadBlob("/reports/aop_budget/details/download", body.filters, "aop-budget-details.xlsx", body.params);
};

/* FTE Report incl. New Hire Plan — see APIIntegrationFiles/FTE_NEW_HIRE_PLAN_API.md. Only the
   filters the backend supports are forwarded; the date range goes in `params`. */
const FTE_FILTER_KEYS = [
  "contractId", "projectId", "clinLvl2", "orgId", "bu", "contractType", "competitionType",
  "awardType", "sector", "popEndDate", "employeeId", "employeeType", "contractName",
  "customerName", "altProjId", "programManager", "pc",
];

function buildFteReportBody(filters = {}, dateRange = {}) {
  const { startMonth, startYear, endMonth, endYear } = dateRange;
  return {
    filters: toReportApiFilters(filters, FTE_FILTER_KEYS),
    params: { startMonth, startYear, endMonth, endYear },
  };
}

function postFteReport(path, filters, dateRange) {
  return fetch(`${BASE}/reports/fte_new_hire_plan/${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify(buildFteReportBody(filters, dateRange)),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const getFteSummaryAPI = (filters, dateRange) => postFteReport("summary", filters, dateRange);
export const getFteDetailsAPI = (filters, dateRange) => postFteReport("details", filters, dateRange);

export const downloadFteSummaryAPI = (filters = {}, dateRange = {}) => {
  const body = buildFteReportBody(filters, dateRange);
  return downloadBlob("/reports/fte_new_hire_plan/summary/download", body.filters, "fte-report-summary.xlsx", body.params);
};

export const downloadFteDetailsAPI = (filters = {}, dateRange = {}) => {
  const body = buildFteReportBody(filters, dateRange);
  return downloadBlob("/reports/fte_new_hire_plan/details/download", body.filters, "fte-report-details.xlsx", body.params);
};

export function getWaterfallViewLevel2API(filters = {}, version = "") {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== "" && v != null,
    ),
  );
  return fetch(`${BASE}/reports/waterfall_eac/waterfall_view_level_2`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({
      filters: cleanFilters,
      params: version ? { version } : {},
    }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

async function downloadBlob(
  path,
  filters = {},
  fallback = "report.xlsx",
  params = {},
) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters, params }),
  });
  if (!res.ok) throw new Error("Download failed");
  const cd = res.headers.get("content-disposition") || "";
  const match = cd.match(/filename[^;=\n]*=["']?([^"'\n;]+)/i);
  const filename = match ? match[1].trim() : fallback;
  const blob = await res.blob();
  return { blob, filename };
}

export const downloadIndirectPLAPI = (f) =>
  downloadBlob("/reports/indirect/indirectpnl/download", f, "indirect-pl.xlsx");
export const downloadFringePLAPI = (f) =>
  downloadBlob("/reports/indirect/fringe/download", f, "fringe-pl.xlsx");
export const downloadSummaryVsBudgetDeptAPI = (f) =>
  downloadBlob(
    "/reports/indirect/summary-vs-budget/download",
    f,
    "summary-vs-budget.xlsx",
  );
export const downloadSummaryVsBudgetFringeAPI = (f) =>
  downloadBlob(
    "/reports/indirect/summary-vs-budget-fringe/download",
    f,
    "summary-vs-budget-fringe.xlsx",
  );
export const downloadSummaryVsPriorDeptAPI = (f) =>
  downloadBlob(
    "/reports/indirect/summary-version-comparison/download",
    f,
    "summary-vs-prior.xlsx",
  );
export const downloadSummaryVsPriorFringeAPI = (f) =>
  downloadBlob(
    "/reports/indirect/summary-version-comparison-fringe/download",
    f,
    "summary-vs-prior-fringe.xlsx",
  );
export const downloadLaborComparisonAPI = (f) =>
  downloadBlob(
    "/reports/indirect/labor/comparison/download",
    f,
    "labor-comparison.xlsx",
  );

/* ── Project Management Reviews (PMR) ────────────────────────────── */
function postPMR(path, filters = {}) {
  const clean = toReportApiFilters(filters, [
    "year",
    "quarter",
    "monthName",
    "contractId",
    "clinLvl2",
    "projectId",
    "bu",
    "sector",
    "awardType",
    "businessType",
    "competitionType",
    "orgId",
    "contractType",
    "contractName",
    "customerName",
    "popEndDate",
  ]);
  return fetch(`${BASE}/reports/project_management_reviews${path}`, {
    method: "POST",
    headers: otherAPIHeaders,
    body: JSON.stringify({ filters: clean, params: {} }),
  })
    .then((res) => res.json())
    .catch((err) => err);
}

export const getPMRProgramInformationAPI = (f) =>
  postPMR("/program_information", f);
export const getPMRProgramPerformanceSlideAPI = (f) =>
  postPMR("/program_performance_slide", f);
export const getPMRForecastAnalysisAPI = (f) =>
  postPMR("/forecast_analysis", f);
export const getPMRDetailsAPI = (f) => postPMR("/pmr_details", f);

function buildPMRFilters(filters = {}) {
  return toReportApiFilters(filters, [
    "year",
    "quarter",
    "monthName",
    "contractId",
    "clinLvl2",
    "projectId",
    "bu",
    "sector",
    "awardType",
    "businessType",
    "competitionType",
    "orgId",
    "contractType",
    "contractName",
    "customerName",
    "popEndDate",
  ]);
}

export const downloadPMRProgramInformationAPI = (f) =>
  downloadBlob(
    "/reports/project_management_reviews/program_information/download",
    buildPMRFilters(f),
    "pmr-program-information.xlsx",
  );
export const downloadPMRForecastAnalysisAPI = (f) =>
  downloadBlob(
    "/reports/project_management_reviews/forecast_analysis/download",
    buildPMRFilters(f),
    "pmr-forecast-analysis.xlsx",
  );
export const downloadPMRDetailsAPI = (f) =>
  downloadBlob(
    "/reports/project_management_reviews/pmr_details/download",
    buildPMRFilters(f),
    "pmr-details.xlsx",
  );
export const downloadEmployeeLaborAPI = (f) =>
  downloadBlob(
    "/reports/indirect/labor/employee/download",
    f,
    "employee-labor.xlsx",
  );
